import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { supabase } from '../../../lib/supabase';
import { useSEO } from '@/hooks/useSEO';

const categoryColors: Record<string, string> = {
  '維修案例': 'bg-blue-100 text-blue-600',
  '優惠活動': 'bg-orange-100 text-orange-600',
  '公司動態': 'bg-green-100 text-green-600',
  '維修教學': 'bg-purple-100 text-purple-600',
};

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: string;
  image: string;
  published_at: string;
  published: boolean;
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [relatedNews, setRelatedNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: article ? article.title : '最新消息',
    description: article
      ? article.summary.slice(0, 155)
      : '兄弟維修最新消息與維修案例分享',
    keywords: article ? `${article.category},手機維修,兄弟維修,台北維修` : '手機維修,兄弟維修',
    canonical: id ? `/news/${id}` : '/news',
    ogImage: article?.image,
    ogType: 'article',
  });

  useEffect(() => {
    if (id) {
      fetchArticle(id);
    }
  }, [id]);

  const fetchArticle = async (articleId: string) => {
    try {
      // Fetch article
      const { data: articleData, error: articleError } = await supabase
        .from('news_articles')
        .select('*')
        .eq('id', articleId)
        .eq('published', true)
        .single();

      if (articleError) throw articleError;
      setArticle(articleData);

      // Fetch related news
      const { data: relatedData, error: relatedError } = await supabase
        .from('news_articles')
        .select('*')
        .eq('published', true)
        .neq('id', articleId)
        .order('published_at', { ascending: false })
        .limit(3);

      if (relatedError) throw relatedError;
      setRelatedNews(relatedData || []);
    } catch (err) {
      console.error('Failed to fetch article:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-20 min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-20 min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl font-bold text-gray-200 mb-4">404</div>
            <p className="text-gray-500 mb-6">找不到此文章</p>
            <button
              onClick={() => navigate('/news')}
              className="px-6 py-2.5 bg-orange-500 text-white rounded-full cursor-pointer"
            >
              返回消息列表
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Image */}
        <div className="w-full h-80 overflow-hidden mt-16">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Article */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <button onClick={() => navigate('/')} className="hover:text-orange-500 cursor-pointer">首頁</button>
              <i className="ri-arrow-right-s-line" />
              <button onClick={() => navigate('/news')} className="hover:text-orange-500 cursor-pointer">最新消息</button>
              <i className="ri-arrow-right-s-line" />
              <span className="text-gray-600 line-clamp-1">{article.title}</span>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <span className={`text-xs px-3 py-1 rounded-full font-medium ${categoryColors[article.category] ?? 'bg-gray-100 text-gray-600'}`}>
                {article.category}
              </span>
              <span className="text-gray-400 text-sm">{article.published_at}</span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8 leading-tight">
              {article.title}
            </h1>

            {/* Content */}
            <style>{`
              .article-content h1 { font-size: 1.75rem; font-weight: 700; margin: 1.5rem 0 0.75rem; color: #111827; }
              .article-content h2 { font-size: 1.4rem; font-weight: 700; margin: 1.25rem 0 0.6rem; color: #111827; }
              .article-content h3 { font-size: 1.15rem; font-weight: 600; margin: 1rem 0 0.5rem; color: #1f2937; }
              .article-content p  { margin: 0.6rem 0; font-size: 0.9375rem; line-height: 1.8; color: #374151; }
              .article-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.75rem 0; }
              .article-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.75rem 0; }
              .article-content li { margin: 0.3rem 0; font-size: 0.9375rem; color: #374151; }
              .article-content strong { font-weight: 700; color: #111827; }
              .article-content em { font-style: italic; }
              .article-content u  { text-decoration: underline; }
              .article-content img { max-width: 100%; border-radius: 12px; margin: 1rem 0; }
              .article-content a  { color: #f97316; text-decoration: underline; }
            `}</style>
            <div
              className="article-content text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-gray-100">
              <button
                onClick={() => navigate('/news')}
                className="flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line" />
                </div>
                返回消息列表
              </button>
            </div>
          </div>
        </section>

        {/* Related */}
        {relatedNews.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">相關文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedNews.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => navigate(`/news/${n.id}`)}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all cursor-pointer"
                  >
                    <div className="h-40 overflow-hidden">
                      <img src={n.image} alt={n.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-4">
                      <div className="text-xs text-gray-400 mb-1">{n.published_at}</div>
                      <h3 className="font-bold text-gray-900 text-sm line-clamp-2">{n.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}